import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplets, Sparkles, ShieldCheck } from 'lucide-react';
import PageShell from '@/components/PageShell';
import PhotoStrip, { galleryImages } from '@/components/PhotoStrip';
import { Button } from '@/components/ui/button';

const faqs = [
  {
    q: 'Is vapor blasting the same as vapour blasting?',
    a: 'Yes. "Vapor blasting" is the American spelling and "vapour blasting" is the New Zealand, Australian and British spelling. Same process, same machine, same result — a fine abrasive suspended in water fired at low pressure against the part.',
  },
  {
    q: 'What else is it called?',
    a: 'Wet blasting, aqua blasting, slurry blasting, liquid honing and dustless blasting all describe closely related processes. In the US, "vapor honing" is common for the same work we do on engine cases and carburettors.',
  },
  {
    q: 'Do you offer vapor blasting in Christchurch?',
    a: 'Yes — our workshop is at Unit 2, 1 Doric Way, Islington, Christchurch. You can drop parts off in person or ship them to us from anywhere in New Zealand.',
  },
  {
    q: 'What kind of finish does vapor blasting leave?',
    a: 'An even satin sheen with the casting detail intact. Because water cushions the media, alloy is not etched, tolerances are not lost and thin sections do not warp.',
  },
];

const VaporBlasting = () => {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Vapor Blasting',
    alternateName: ['Vapour Blasting', 'Vapor Honing', 'Wet Blasting', 'Aqua Blasting'],
    serviceType: 'Vapor blasting (vapour blasting) surface restoration',
    description:
      'Vapor blasting — spelled vapour blasting in New Zealand — for engine cases, carburettors, cylinder heads, marine and industrial alloy parts. Christchurch, New Zealand.',
    areaServed: [
      { '@type': 'City', name: 'Christchurch' },
      { '@type': 'AdministrativeArea', name: 'Canterbury' },
      { '@type': 'Country', name: 'New Zealand' },
    ],
    provider: {
      '@type': 'LocalBusiness',
      '@id': 'https://precisionvapour.co.nz/#business',
      name: 'Precision Vapour Blasting',
      alternateName: 'Precision Vapor Blasting',
      url: 'https://precisionvapour.co.nz',
      telephone: '+64226578662',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Unit 2, 1 Doric Way, Islington',
        addressLocality: 'Christchurch',
        addressRegion: 'Canterbury',
        postalCode: '8042',
        addressCountry: 'NZ',
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <PageShell
      title="Vapor Blasting Christchurch NZ | Vapor Honing Specialists"
      description="Vapor blasting (vapour blasting) in Christchurch, New Zealand. Vapor honing for engine cases, carburettors, heads, marine and industrial alloy. Free quotes."
      canonicalPath="/vapor-blasting"
      crumbs={[{ name: 'Vapor Blasting', path: '/vapor-blasting' }]}
      jsonLd={[serviceSchema, faqSchema]}
    >
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto max-w-4xl">
          <p className="text-primary font-medium tracking-[0.3em] uppercase mb-4 text-sm">
            US spelling
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
            Vapor Blasting <span className="gradient-text">in New Zealand</span>
          </h1>
          <p className="text-lg text-muted-foreground mb-6">
            If you learned the term from an American workshop, YouTube channel or forum, you are
            probably searching for <strong className="text-foreground">vapor blasting</strong> or{' '}
            <strong className="text-foreground">vapor honing</strong>. Here in New Zealand we spell
            it <strong className="text-foreground">vapour blasting</strong> — it is the exact same
            process, and it is what we do every day at our Islington workshop in Christchurch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <Button variant="hero" asChild>
              <Link to="/contact">Get a free quote</Link>
            </Button>
            <Button variant="heroOutline" asChild>
              <Link to="/services">See our services</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="pb-4 bg-background">
        <div className="container-wide mx-auto max-w-4xl">
          <h2 className="font-display text-3xl font-bold mb-4 text-foreground">
            Vapor or vapour — one process, two spellings
          </h2>
          <p className="text-muted-foreground mb-4">
            American English drops the "u", so the same trade is written vapor blasting in the
            United States and vapour blasting across New Zealand, Australia and the UK. Neither is
            more correct, and neither describes a different machine. Both refer to a low-pressure
            slurry of fine abrasive and water, fired through a nozzle so the water cushions each
            particle as it strikes the surface.
          </p>
          <p className="text-muted-foreground mb-4">
            You will also see the process called wet blasting, aqua blasting, slurry blasting,
            liquid honing and — most commonly in the US — vapor honing. If you have been quoted for
            any of those, we can do the work.
          </p>

          <h2 className="font-display text-3xl font-bold mb-4 mt-10 text-foreground">
            What vapor blasting actually does
          </h2>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {[
              {
                icon: Droplets,
                title: 'Water-cushioned media',
                text: 'The abrasive never hits the metal dry, so alloy is not etched and thin castings do not distort.',
              },
              {
                icon: Sparkles,
                title: 'Even satin finish',
                text: 'Casting marks, part numbers and machined faces survive intact — the surface just looks new again.',
              },
              {
                icon: ShieldCheck,
                title: 'Genuine decontamination',
                text: 'Oil, carbon, salt and old coatings are flushed out of porosity rather than driven deeper in.',
              },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="glass-card rounded-xl p-6">
                <Icon className="w-6 h-6 text-primary mb-3" />
                <h3 className="font-display text-lg font-bold text-foreground mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground">{text}</p>
              </div>
            ))}
          </div>

          <PhotoStrip
            images={[
              galleryImages.carb1,
              galleryImages.head,
              galleryImages.detail,
              galleryImages.holley,
            ]}
            heading="Vapor blasted parts from our workshop"
            blurb="Motorcycle and car components restored by vapor (vapour) blasting in Christchurch."
          />

          <h2 className="font-display text-3xl font-bold mb-4 text-foreground">
            Parts we vapor blast
          </h2>
          <ul className="text-muted-foreground space-y-2 mb-6 list-disc pl-5">
            <li>Motorcycle and car engine cases, cylinder heads and barrels</li>
            <li>Carburettors — Mikuni, Keihin, Holley, Weber, SU and more</li>
            <li>Alloy wheels, brake components, hubs and brackets</li>
            <li>Outboard powerheads, gear cases, props and stainless marine hardware</li>
            <li>Industrial tooling, jigs, moulds and architectural fittings</li>
          </ul>
          <p className="text-muted-foreground mb-6">
            Full details are on our{' '}
            <Link to="/services" className="text-primary hover:underline">
              vapour blasting services
            </Link>{' '}
            page, and the{' '}
            <Link to="/process" className="text-primary hover:underline">
              step-by-step process
            </Link>{' '}
            page walks through exactly what happens to your parts. If you were originally looking
            at sandblasting, our{' '}
            <Link to="/vapour-blasting-vs-sandblasting" className="text-primary hover:underline">
              vapour blasting vs sandblasting comparison
            </Link>{' '}
            explains the difference in plain terms.
          </p>

          <h2 className="font-display text-3xl font-bold mb-4 mt-10 text-foreground">
            Vapor blasting FAQ
          </h2>
          <div className="space-y-4 mb-10">
            {faqs.map((f) => (
              <div key={f.q} className="glass-card rounded-xl p-6">
                <h3 className="font-display text-lg font-bold text-foreground mb-2">{f.q}</h3>
                <p className="text-muted-foreground text-sm">{f.a}</p>
              </div>
            ))}
          </div>

          <div className="glass-card rounded-xl p-8 text-center">
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-3 text-foreground">
              Send us your parts
            </h2>
            <p className="text-muted-foreground mb-6">
              Drop them off at Unit 2, 1 Doric Way, Islington, Christchurch, or ship them from
              anywhere in New Zealand. Tell us what you have and we will quote it.
            </p>
            <Button variant="hero" asChild>
              <Link to="/contact">
                Get a free quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default VaporBlasting;
